import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useService } from 'react-service-locator';
import { match } from 'ts-pattern';
import {
  AvailableComparePeriodsForPeriod,
  getAvailableComparePeriodsForPeriod,
} from '@lgg/isomorphic/dashboard-date-helpers';
import { dayjs, Dayjs } from '@lgg/isomorphic/dayjs';
import {
  MetricComparePeriodInput,
  MetricPeriodWhereInput,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { AuthorizationService } from 'src/services/authorization.service';

export enum BaseDateOption {
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  LAST_7_DAYS = 'LAST_7_DAYS',
  LAST_4_WEEKS = 'LAST_4_WEEKS',
  LAST_3_MONTHS = 'LAST_3_MONTHS',
  LAST_12_MONTHS = 'LAST_12_MONTHS',
  MONTH_TO_DATE = 'MONTH_TO_DATE',
  QUARTER_TO_DATE = 'QUARTER_TO_DATE',
  YEAR_TO_DATE = 'YEAR_TO_DATE',
  CUSTOM = 'CUSTOM',
}

export enum ComparedToDateOption {
  PREVIOUS_PERIOD = 'PREVIOUS_PERIOD',
  PREVIOUS_MONTH = 'PREVIOUS_MONTH',
  PREVIOUS_QUARTER = 'PREVIOUS_QUARTER',
  PREVIOUS_YEAR = 'PREVIOUS_YEAR',
  CUSTOM = 'CUSTOM',
}

type DateRange = {
  fromDate: Date;
  toDate: Date;
};

type DayJsDateRange = {
  fromDate: Dayjs;
  toDate: Dayjs;
};

type GetSelectedComparePeriodInfoProps = {
  fromDate: Date;
  toDate: Date;
  currentComparePeriod: ComparedToDateOption;
};

export type PeriodFilterContextProps = {
  baseDateFilter: BaseDateOption;
  setBaseDateFilter: Dispatch<SetStateAction<BaseDateOption>>;
  baseDateFilterDates: DateRange;
  setBaseDateFilterDates: Dispatch<SetStateAction<DateRange>>;
  comparedToDateFilter: ComparedToDateOption;
  setComparedToDateFilter: Dispatch<SetStateAction<ComparedToDateOption>>;
  comparedToDateFilterDates: DateRange;
  setComparedToDateFilterDates: Dispatch<SetStateAction<DateRange>>;
  metricPeriodWhereInput: MetricPeriodWhereInput;
  metricComparePeriodWhereInput: MetricComparePeriodInput;
  availableComparePeriods: AvailableComparePeriodsForPeriod;
  getSelectedComparePeriodInfo: (props: GetSelectedComparePeriodInfoProps) => {
    availableComparePeriods: AvailableComparePeriodsForPeriod;
    selectedCompareRange: DayJsDateRange | null;
  };
  resetDateFilters: VoidFunction;
  baseDateFilterDefaultValue: DateRange;
};

export const periodFilterContextDefaultValue: PeriodFilterContextProps = {
  baseDateFilter: BaseDateOption.TODAY,
  setBaseDateFilter: () => {},
  baseDateFilterDates: {
    fromDate: new Date(),
    toDate: new Date(),
  },
  baseDateFilterDefaultValue: {
    fromDate: new Date(),
    toDate: new Date(),
  },
  setBaseDateFilterDates: () => {},
  comparedToDateFilter: ComparedToDateOption.PREVIOUS_PERIOD,
  setComparedToDateFilter: () => {},
  comparedToDateFilterDates: {
    fromDate: new Date(),
    toDate: new Date(),
  },
  setComparedToDateFilterDates: () => {},
  metricPeriodWhereInput: {},
  metricComparePeriodWhereInput: {},
  availableComparePeriods: {
    previousPeriod: {
      fromDate: dayjs(),
      toDate: dayjs(),
    },
  },
  getSelectedComparePeriodInfo: () => ({
    availableComparePeriods: {
      previousPeriod: {
        fromDate: dayjs(),
        toDate: dayjs(),
      },
    },
    selectedCompareRange: {
      fromDate: dayjs(),
      toDate: dayjs(),
    },
  }),
  resetDateFilters: () => {},
};

export const PeriodFilterContext = React.createContext<PeriodFilterContextProps>(
  periodFilterContextDefaultValue,
);

type PeriodFilterContextProviderProps = {
  children: React.ReactNode;
};

export const PeriodFilterContextProvider = memo(
  ({ children }: PeriodFilterContextProviderProps) => {
    const authState = useService(AuthorizationService);
    const timezone = authState.getTimezone();
    const { startOfDay, endOfDay, initDayJsDate } = useDateHelpers();
    const [baseDateFilter, setBaseDateFilter] = useState<BaseDateOption>(
      BaseDateOption.TODAY,
    );

    const baseDateFilterDefaultValue = useMemo(
      () => ({
        fromDate: startOfDay(new Date()),
        toDate: endOfDay(new Date()),
      }),
      [endOfDay, startOfDay],
    );

    const availableComparePeriodsDefaultValue = useMemo(
      () =>
        getAvailableComparePeriodsForPeriod({
          fromDate: baseDateFilterDefaultValue.fromDate.toISOString(),
          toDate: baseDateFilterDefaultValue.toDate.toISOString(),
          timezone,
        }),
      [baseDateFilterDefaultValue.fromDate, baseDateFilterDefaultValue.toDate, timezone],
    );

    const [baseDateFilterDates, setBaseDateFilterDates] = useState<DateRange>(
      baseDateFilterDefaultValue,
    );
    const [comparedToDateFilter, setComparedToDateFilter] =
      useState<ComparedToDateOption>(ComparedToDateOption.PREVIOUS_PERIOD);
    const [availableComparePeriods, setAvailableComparePeriods] = useState<{
      previousPeriod: DayJsDateRange;
      previousMonth?: DayJsDateRange;
      previousQuarter?: DayJsDateRange;
      previousYear?: DayJsDateRange;
    }>(availableComparePeriodsDefaultValue);

    const { previousPeriod: previousPeriodData } = availableComparePeriods;

    const [comparedToDateFilterDates, setComparedToDateFilterDates] = useState<DateRange>(
      {
        fromDate: previousPeriodData.fromDate.toDate(),
        toDate: previousPeriodData.toDate.toDate(),
      },
    );

    const [metricPeriodWhereInput, setMetricPeriodWhereInput] =
      useState<MetricPeriodWhereInput>({
        _isToday: true,
      });

    const [metricComparePeriodWhereInput, setMetricComparePeriodWhereInput] =
      useState<MetricComparePeriodInput>({
        _previousPeriod: true,
      });

    const getSelectedComparePeriodInfo = useCallback(
      ({ fromDate, toDate, currentComparePeriod }: GetSelectedComparePeriodInfoProps) => {
        const availableComparePeriods = getAvailableComparePeriodsForPeriod({
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
          timezone,
        });

        const selectedCompareRange = match(currentComparePeriod)
          .with(
            ComparedToDateOption.PREVIOUS_YEAR,
            () => availableComparePeriods.previousYear,
          )
          .with(
            ComparedToDateOption.PREVIOUS_MONTH,
            () => availableComparePeriods.previousMonth,
          )
          .with(
            ComparedToDateOption.PREVIOUS_QUARTER,
            () => availableComparePeriods.previousQuarter,
          )
          .with(
            ComparedToDateOption.PREVIOUS_PERIOD,
            () => availableComparePeriods.previousPeriod,
          )
          .with(ComparedToDateOption.CUSTOM, () => null)
          .otherwise(() => null);

        return {
          availableComparePeriods,
          selectedCompareRange: selectedCompareRange ?? null,
        };
      },
      [timezone],
    );

    const resetDateFilters = useCallback(() => {
      setBaseDateFilterDates(baseDateFilterDefaultValue);
      setAvailableComparePeriods(availableComparePeriodsDefaultValue);
      setBaseDateFilter(BaseDateOption.TODAY);
      setComparedToDateFilter(ComparedToDateOption.PREVIOUS_PERIOD);
      setComparedToDateFilterDates({
        fromDate: availableComparePeriodsDefaultValue.previousPeriod.fromDate.toDate(),
        toDate: availableComparePeriodsDefaultValue.previousPeriod.toDate.toDate(),
      });
    }, [availableComparePeriodsDefaultValue, baseDateFilterDefaultValue]);

    useEffect(() => {
      const { availableComparePeriods, selectedCompareRange } =
        getSelectedComparePeriodInfo({
          fromDate: baseDateFilterDates.fromDate,
          toDate: baseDateFilterDates.toDate,
          currentComparePeriod: comparedToDateFilter,
        });

      if (!selectedCompareRange) {
        setComparedToDateFilter(ComparedToDateOption.PREVIOUS_PERIOD);
        setComparedToDateFilterDates({
          fromDate: availableComparePeriods.previousPeriod.fromDate.toDate(),
          toDate: availableComparePeriods.previousPeriod.toDate.toDate(),
        });
      } else {
        setComparedToDateFilterDates({
          fromDate: selectedCompareRange.fromDate.toDate(),
          toDate: selectedCompareRange.toDate.toDate(),
        });
      }

      setAvailableComparePeriods(availableComparePeriods);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseDateFilterDates.fromDate, baseDateFilterDates.toDate, timezone]);

    useEffect(() => {
      // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
      const value: MetricPeriodWhereInput = match(baseDateFilter)
        .with(BaseDateOption.TODAY, () => ({
          _isToday: true,
        }))
        .with(BaseDateOption.YESTERDAY, () => ({
          _isYesterday: true,
        }))
        .with(BaseDateOption.LAST_7_DAYS, () => ({
          _isLastXDays: 7,
        }))
        .with(BaseDateOption.LAST_4_WEEKS, () => ({
          _isLastXWeeks: 4,
        }))
        .with(BaseDateOption.LAST_3_MONTHS, () => ({
          _isLastXMonths: 3,
        }))
        .with(BaseDateOption.LAST_12_MONTHS, () => ({
          _isLastXMonths: 12,
        }))
        .with(BaseDateOption.MONTH_TO_DATE, () => ({
          _isThisMonth: true,
        }))
        .with(BaseDateOption.QUARTER_TO_DATE, () => ({
          _isThisQuarter: true,
        }))
        .with(BaseDateOption.YEAR_TO_DATE, () => ({
          _isThisYear: true,
        }))
        .with(BaseDateOption.CUSTOM, () => ({
          _isBetween: {
            from: baseDateFilterDates.fromDate,
            to: baseDateFilterDates.toDate,
          },
        }))
        .exhaustive();
      setMetricPeriodWhereInput(value);
    }, [
      baseDateFilter,
      baseDateFilterDates.fromDate,
      baseDateFilterDates.toDate,
      initDayJsDate,
    ]);

    useEffect(() => {
      // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
      const value: MetricComparePeriodInput = match(comparedToDateFilter)
        .with(ComparedToDateOption.PREVIOUS_PERIOD, () => ({
          _previousPeriod: true,
        }))
        .with(ComparedToDateOption.PREVIOUS_MONTH, () => ({
          _previousMonth: true,
        }))
        .with(ComparedToDateOption.PREVIOUS_QUARTER, () => ({
          _previousQuarter: true,
        }))
        .with(ComparedToDateOption.PREVIOUS_YEAR, () => ({
          _previousYear: true,
        }))
        .with(ComparedToDateOption.CUSTOM, () => ({
          _customStartAt: comparedToDateFilterDates.fromDate.toISOString(),
        }))
        .exhaustive();

      setMetricComparePeriodWhereInput(value);
    }, [
      comparedToDateFilter,
      comparedToDateFilterDates.fromDate,
      comparedToDateFilterDates.toDate,
    ]);

    return (
      <PeriodFilterContext.Provider
        value={{
          baseDateFilter,
          setBaseDateFilter,
          baseDateFilterDates,
          setBaseDateFilterDates,
          comparedToDateFilter,
          setComparedToDateFilter,
          comparedToDateFilterDates,
          setComparedToDateFilterDates,
          metricPeriodWhereInput,
          metricComparePeriodWhereInput,
          availableComparePeriods,
          getSelectedComparePeriodInfo,
          resetDateFilters,
          baseDateFilterDefaultValue,
        }}
      >
        {children}
      </PeriodFilterContext.Provider>
    );
  },
);
